import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Badge, Empty, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, dayMonth } from '../../utils/format';

const TONE = {
  pending: colors.warning,
  confirmed: colors.success,
  checked_in: colors.success,
  completed: colors.ink2,
  rejected: colors.danger,
  cancelled: colors.danger,
};

/** Every stay the guest has booked, with what they can still do about it. */
const MyStaysPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.hotels.myBookings({ limit: 40 })
      .then((r) => setStays(r.data || []))
      .catch(() => setStays([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const cancel = async (stay) => {
    const hours = (stay.hotel && stay.hotel.cancellation && stay.hotel.cancellation.freeUntilHours) ?? 24;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Cancel this stay? Free cancellation up to ${hours} hours before check-in.`)) return;
    try {
      const res = await api.hotels.cancel(stay._id, { reason: 'Plans changed' });
      toast.success(res.message, 'Cancelled');
      load();
    } catch (err) {
      toast.error(err.message, 'Could not cancel');
    }
  };

  return (
    <div>
      <PageHeader title="My stays" subtitle={`${stays.length} bookings`} />

      {loading ? <Loading /> : !stays.length ? (
        <Empty
          emoji="🏨"
          title="No stays yet"
          text="Book a hotel on KAAMWALA and every booking shows up here."
          actionLabel="Find hotels"
          onAction={() => navigate('/hotels')}
        />
      ) : (
        <div className="grid grid-list">
          {stays.map((item, i) => (
            <Reveal key={item._id} delay={stagger(i, 45)} className="card sh-sm">
              <div className="row-between">
                <div className="t-title clamp-1 grow">{(item.hotel && item.hotel.name) || 'Hotel'}</div>
                <Badge value={item.status.replace('_', ' ')} color={TONE[item.status] || colors.ink2} />
              </div>
              <div className="t-tiny">Booking {item.bookingNo}</div>

              <div className="row" style={{ gap: 18, background: 'var(--bg)', borderRadius: 12, padding: 12, margin: '10px 0' }}>
                <div>
                  <div className="t-tiny">CHECK-IN</div>
                  <div className="t-body">{dayMonth(item.checkIn)}</div>
                  <div className="t-tiny">from {(item.hotel && item.hotel.checkInTime) || '13:00'}</div>
                </div>
                <div style={{ fontSize: 18 }}>→</div>
                <div>
                  <div className="t-tiny">CHECK-OUT</div>
                  <div className="t-body">{dayMonth(item.checkOut)}</div>
                  <div className="t-tiny">by {(item.hotel && item.hotel.checkOutTime) || '11:00'}</div>
                </div>
                <div className="grow" />
                <div style={{ textAlign: 'right' }}>
                  <div className="t-tiny">{item.nights} night{item.nights > 1 ? 's' : ''}</div>
                  <div className="t-body" style={{ fontWeight: 800 }}>{currency(item.totalAmount)}</div>
                  <div className="t-tiny">{item.paymentMode === 'cash' ? 'Pay at hotel' : item.paymentStatus}</div>
                </div>
              </div>

              <div className="t-small">
                {item.room && item.room.title} · {item.roomsBooked} room{item.roomsBooked > 1 ? 's' : ''}
                {' · '}{item.guests && item.guests.adults} guests
              </div>

              {item.cancellation && item.cancellation.refundAmount ? (
                <div className="t-tiny" style={{ color: 'var(--success)', marginTop: 4 }}>
                  Refund {currency(item.cancellation.refundAmount)} · {item.cancellation.refundStatus}
                </div>
              ) : null}
              {item.rejectionReason ? (
                <div className="t-tiny" style={{ color: 'var(--danger)', marginTop: 4 }}>{item.rejectionReason}</div>
              ) : null}

              <div className="row" style={{ gap: 16, marginTop: 10 }}>
                {item.hotel && item.hotel.phone ? (
                  <a className="link-btn" href={`tel:${item.hotel.phone}`}>📞 Call hotel</a>
                ) : null}
                {['pending', 'confirmed'].includes(item.status) ? (
                  <button type="button" className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => cancel(item)}>
                    Cancel stay
                  </button>
                ) : null}
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyStaysPage;
